import { useCallback, useEffect, useState } from "react";
import {
  CalendarRange, FileText, Globe2, HandCoins, HelpCircle, Image, Inbox, KeyRound, LayoutGrid, Megaphone, MessageSquareQuote, ShoppingCart, Store,
  Phone, Receipt, Scale, Package, Tags, Loader2, Users,
} from "lucide-react";
import { DashboardShell } from "../components/layout/DashboardShell";
import { Overview } from "../components/admin/Overview";
import { DistributorsPanel } from "../components/admin/DistributorsPanel";
import { PaymentsPanel } from "../components/admin/PaymentsPanel";
import { MlmCommissionsPanel } from "../components/admin/MlmCommissionsPanel";
import { AccessCodesPanel } from "../components/admin/AccessCodesPanel";
import { CodeOrdersPanel } from "../components/admin/CodeOrdersPanel";
import { ClientSalesPanel } from "../components/admin/ClientSalesPanel";
import { MonthlyCutsPanel } from "../components/admin/MonthlyCutsPanel";
import { LeadsPanel } from "../components/admin/LeadsPanel";
import { MarketingAdminPanel } from "../components/admin/MarketingAdminPanel";
import { ContentPanel } from "../components/admin/ContentPanel";
import { CollectionPanel } from "../components/admin/CollectionPanel";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { DEFAULT_CONTENT, deepMerge } from "../lib/content";

const TEXT_KEYS = ["hero", "marquee", "principio", "soluciones", "pasos", "celular", "dinero", "filosofia", "manifiesto", "testimonios", "faq", "cta", "registro", "footer"];

const F = {
  order: { key: "order", label: "Orden", type: "number" },
  active: { key: "active", label: "Activo", type: "bool" },
};

const COLLECTIONS = {
  servicios: {
    name: "services", title: "Servicios y precios", titleKey: "title", subtitleKey: "description",
    description: "Agrega, edita, activa o desactiva las soluciones que aparecen en “Tecnología para empresas”.",
    fields: [
      { key: "title", label: "Nombre", type: "text" }, { key: "description", label: "Descripción", type: "textarea" },
      { key: "icon", label: "Icono", type: "icon" }, { key: "price", label: "Precio (ej. $49 USD/mes). Vacío = “Precio a consultar”", type: "text" },
      { key: "price_note", label: "Nota de precio", type: "text" }, { key: "image", label: "URL de imagen (opcional)", type: "text" }, F.order, F.active,
    ],
  },
  planes: {
    name: "plans", title: "Planes", titleKey: "name", subtitleKey: "price",
    description: "Planes de servicio que se muestran bajo las soluciones (solo los activos).",
    fields: [
      { key: "name", label: "Nombre", type: "text" }, { key: "price", label: "Precio", type: "text" }, { key: "period", label: "Periodo (ej. /mes)", type: "text" },
      { key: "description", label: "Descripción", type: "textarea" }, { key: "features", label: "Características (una por línea)", type: "lines" },
      { key: "highlighted", label: "Destacado", type: "bool" }, F.order, F.active,
    ],
  },
  faq: {
    name: "faqs", title: "Preguntas frecuentes", titleKey: "question", subtitleKey: "answer",
    fields: [{ key: "question", label: "Pregunta", type: "text" }, { key: "answer", label: "Respuesta", type: "textarea" }, F.order, F.active],
  },
  testimonios: {
    name: "testimonials", title: "Testimonios", titleKey: "name", subtitleKey: "quote",
    description: "La sección de testimonios aparece en la página cuando hay al menos uno activo.",
    fields: [
      { key: "name", label: "Nombre", type: "text" }, { key: "role", label: "Rol", type: "text" }, { key: "company", label: "Empresa", type: "text" },
      { key: "quote", label: "Testimonio", type: "textarea" }, { key: "image", label: "URL de foto (opcional)", type: "text" }, F.order, F.active,
    ],
  },
};

const CONTENT_TABS = {
  textos: { keys: TEXT_KEYS, title: "Textos de la página", description: "Edita cada sección. Los cambios se publican al guardar." },
  medios: { keys: ["media"], title: "Imágenes y videos", description: "Pega URLs de imágenes o un enlace de YouTube / Vimeo / .mp4." },
  paises: { keys: ["paises"], title: "Países disponibles", description: "Lista de países del formulario de registro. Las etiquetas del formulario se editan en Textos → Formulario de registro." },
  legal: { keys: ["legal"], title: "Legal", description: "Términos y condiciones, aviso de privacidad e información legal." },
  contacto: { keys: ["contacto"], title: "Datos de contacto", description: "Correo, WhatsApp, dirección y redes sociales del pie de página." },
};

const TABS = [
  ["resumen", "Resumen", LayoutGrid], ["distribuidores", "Distribuidores", Users], ["pagos", "Pagos de membresía", Receipt],
  ["comisiones", "Comisiones Membresía", HandCoins], ["mensuales", "Cortes mensuales", CalendarRange], ["clientes", "Clientes", Store], ["codigos", "Códigos de acceso", KeyRound], ["pedidos", "Pedidos de códigos", ShoppingCart], ["registros", "Interesados", Inbox],
  ["textos", "Textos", FileText], ["medios", "Imágenes y videos", Image],
  ["servicios", "Servicios y precios", Package], ["planes", "Planes", Tags],
  ["faq", "Preguntas frecuentes", HelpCircle], ["testimonios", "Testimonios", MessageSquareQuote], ["paises", "Países y formulario", Globe2],
  ["legal", "Legal", Scale], ["contacto", "Contacto", Phone], ["marketing", "Marketing", Megaphone],
];

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("resumen");
  const [content, setContent] = useState(null);
  const [pending, setPending] = useState({});

  const loadPending = useCallback(() => {
    api.get("/admin/mlm/stats").then(({ data }) => setPending({ pagos: data.payments_pending_review || 0, pedidos: data.code_orders_pending || 0 })).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    loadPending();
    api
      .get("/admin/content")
      .then(({ data }) => alive && setContent(deepMerge(DEFAULT_CONTENT, data?.data ?? {})))
      .catch(() => alive && setContent(DEFAULT_CONTENT));
    return () => {
      alive = false;
    };
  }, [loadPending]);

  const renderPanel = () => {
    if (tab === "resumen") return <Overview goTo={setTab} />;
    if (tab === "distribuidores") return <DistributorsPanel />;
    if (tab === "pagos") return <PaymentsPanel onChanged={loadPending} />;
    if (tab === "comisiones") return <MlmCommissionsPanel />;
    if (tab === "mensuales") return <MonthlyCutsPanel />;
    if (tab === "clientes") return <ClientSalesPanel />;
    if (tab === "codigos") return <AccessCodesPanel />;
    if (tab === "pedidos") return <CodeOrdersPanel onChanged={loadPending} goTo={setTab} />;
    if (tab === "registros") return <LeadsPanel />;
    if (tab === "marketing") return <MarketingAdminPanel />;
    if (COLLECTIONS[tab]) return <CollectionPanel key={tab} {...COLLECTIONS[tab]} />;
    if (!content) return <Loader2 className="h-6 w-6 animate-spin" />;
    return <ContentPanel key={tab} {...CONTENT_TABS[tab]} content={content} setContent={setContent} />;
  };

  const tabs = TABS.map((t) => (pending[t[0]] ? [...t, pending[t[0]]] : t));

  return (
    <DashboardShell
      testId="admin"
      tabs={tabs}
      tab={tab}
      setTab={setTab}
      subtitle="Panel administrativo"
      onLogout={logout}
      footer={<p className="truncate text-xs text-[#AEB7D3]" data-testid="admin-user-phone">Celular: {user?.phone}</p>}
    >
      {renderPanel()}
    </DashboardShell>
  );
}
